import * as React from "react";
import { cn } from "@/lib/utils";
import Stat, { hasStatValue } from "./Stat";

interface StatItem {
  label: React.ReactNode;
  value?: React.ReactNode | null;
  sub?: string;
}

interface StatGridProps {
  items: StatItem[];
  /** Column count per row. Defaults to min(items.length, 4). */
  cols?: number;
}

/**
 * Hairline-separated grid of stats (the Explore/hero stat-strip pattern).
 *
 * Renders rows of flex items rather than a CSS grid so there are no phantom
 * empty-slot cells when items don't fill the last row — a 6-stat "by the
 * numbers" block has no dangling shaded cell in position 7 or 8.
 *
 * The 4-stat Performance row renders identically to the old layout.
 */
const StatGrid = ({ items, cols }: StatGridProps) => {
  // Empty tiles are dropped, not dashed; a grid with nothing to show is omitted.
  const visible = items.filter((s) => hasStatValue(s.value));
  if (visible.length === 0) return null;
  const n = Math.min(cols || 4, visible.length);

  // Chunk items into rows of n so partial last rows have no phantom background.
  const rows: StatItem[][] = [];
  for (let i = 0; i < visible.length; i += n) {
    rows.push(visible.slice(i, i + n));
  }

  return (
    <div className="overflow-hidden rounded-[var(--radius-md)] border border-border">
      {rows.map((row, ri) => (
        // Below md a row of up to four wraps two-up (four tiles do not fit in
        // 375px — Item 17); from md it is the single hairline row. The rules
        // follow the wrap: on the phone the second tile of each pair carries
        // the left rule and the second pair the top rule; from md every tile
        // after the first carries the left rule and none the top.
        <div
          key={ri}
          className={cn("flex flex-wrap md:flex-nowrap", ri > 0 && "border-t border-border")}
        >
          {row.map((s, ci) => (
            <div
              key={ci}
              data-stat-tile=""
              className={cn(
                "grow shrink basis-1/2 md:basis-0 bg-card px-4 sm:px-5 py-[18px] border-border",
                "[&:nth-child(even)]:border-l [&:nth-child(n+3)]:border-t",
                "md:[&:nth-child(n+2)]:border-l md:[&:nth-child(n+3)]:border-t-0",
              )}
            >
              <Stat label={s.label} value={s.value} sub={s.sub} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default StatGrid;
